import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './input';
import { searchCustomers, type SearchCustomersResponse } from '@vn-propane/shared/client';
import type { Customer } from '@vn-propane/shared/client';
import { Search, Loader2, User, Phone, MapPin, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

// Type for search result (customer with optional address)
type CustomerSearchResult = SearchCustomersResponse['data'][number];

interface CustomerTypeaheadProps {
  organizationId: string;
  value?: Customer | null;
  onChange: (customer: Customer | null) => void;
  onCreateNew?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function CustomerTypeahead({
  organizationId,
  value,
  onChange,
  onCreateNew,
  placeholder,
  className,
  disabled = false,
}: CustomerTypeaheadProps) {
  const { t } = useTranslation(['admin']);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchForCustomers = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const result = await searchCustomers({
        path: { org_id: organizationId },
        query: { q: searchQuery, limit: 10 },
      });

      if (result.data) {
        setResults(result.data.data);
      }
    } catch (err) {
      console.error('Failed to search customers:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchForCustomers(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchForCustomers]);

  const handleSelect = (customer: CustomerSearchResult) => {
    // Convert to Customer type (without address) for the value
    const customerValue: Customer = {
      id: customer.id,
      organizationId: customer.organizationId,
      fullName: customer.fullName,
      phone: customer.phone,
      addressId: customer.address?.id || undefined,
      isRegistered: customer.isRegistered,
      customerUserId: customer.customerUserId,
    };
    onChange(customerValue);
    setQuery('');
    setIsOpen(false);
    setResults([]);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    inputRef.current?.focus();
  };

  if (value) {
    return (
      <div className={cn('relative', className)}>
        <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50">
          <User className="w-4 h-4 text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{value.fullName}</p>
            {value.phone && (
              <p className="text-xs text-muted-foreground">{value.phone}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="text-muted-foreground hover:text-foreground text-sm"
          >
            {t('customerTypeahead.change')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        {loading ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        )}
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder || t('customerTypeahead.searchPlaceholder')}
          disabled={disabled}
          className="pl-10"
        />
      </div>

      {isOpen && (query.trim() || onCreateNew) && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-auto">
          {results.length > 0 ? (
            <ul className="py-1">
              {results.map((customer) => (
                <li key={customer.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(customer)}
                    className="w-full px-3 py-2 text-left hover:bg-muted transition-colors"
                  >
                    <div className="font-medium">{customer.fullName}</div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {customer.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          <span>{customer.phone}</span>
                        </div>
                      )}
                      {customer.address?.formattedAddress && (
                        <div className="flex items-start gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                          <span className="truncate">{customer.address.formattedAddress}</span>
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : query.trim() && !loading ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {t('customerTypeahead.noCustomersFound')}
            </div>
          ) : null}

          {onCreateNew && (
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onCreateNew();
              }}
              className="w-full px-3 py-2 text-left hover:bg-muted transition-colors border-t flex items-center gap-2 text-primary"
            >
              <Plus className="w-4 h-4" />
              {t('customerTypeahead.createNewCustomer')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
